import React, { useMemo, useState } from 'react';
import { Box, Card, CardContent, Container, Grid, Stack, Typography } from '@mui/material';
import { getImageUrl } from '../services/api';
import Seo from './Seo';

const resolveImageUrl = (imageName) => getImageUrl(imageName, 'logos');

const InfoListingPage = ({ title, description, items, reverseLayout = false, smallImage = false }) => {
  const normalizedItems = useMemo(
    () => items.map((item) => ({ ...item, imageUrl: resolveImageUrl(item.image) })),
    [items]
  );

  const [activeIndex, setActiveIndex] = useState(0);

  const activeItem = normalizedItems[activeIndex] || normalizedItems[0];

  return (
    <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 8 }}>
      <Seo
        title={title}
        description={description}
        keywords={`${title}, Grosvenor Plymouth, Plymouth hotel, Plymouth attractions, Plymouth visitor guide`}
        author="Grosvenor Plymouth"
      />

      <Container maxWidth="xl">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h2"
            sx={{
              fontFamily: 'Playfair Display',
              fontWeight: 700,
              color: 'primary.main',
              mb: 2,
              fontSize: { xs: '2.2rem', md: '3.2rem' }
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              maxWidth: 860,
              mx: 'auto',
              color: '#4b5563',
              lineHeight: 1.85,
              fontSize: { xs: '1rem', md: '1.05rem' }
            }}
          >
            {description}
          </Typography>
        </Box>

        <Card
          sx={{
            borderRadius: 5,
            overflow: 'hidden',
            boxShadow: '0 18px 45px rgba(15, 23, 42, 0.08)',
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)'
          }}
        >
          <Grid container direction={reverseLayout ? 'row-reverse' : 'row'}>
            <Grid item xs={12} md={smallImage ? 4 : 5}>
              <Box sx={{ p: { xs: 2, md: 3 }, height: '100%' }}>
                <Box
                  component="img"
                  src={activeItem?.imageUrl}
                  alt={activeItem?.title}
                  sx={{
                    width: '100%',
                    height: smallImage ? { xs: 220, md: 240 } : { xs: 280, md: 420 },
                    objectFit: 'cover',
                    borderRadius: 4,
                    boxShadow: '0 12px 30px rgba(15, 23, 42, 0.18)',
                    mb: 2.5
                  }}
                />

                <Grid container spacing={1.5}>
                  {normalizedItems.map((item, index) => (
                    <Grid item xs={smallImage ? 6 : 4} sm={smallImage ? 4 : 3} md={smallImage ? 6 : 4} key={item.title}>
                      <Box
                        onMouseEnter={() => setActiveIndex(index)}
                        onClick={() => setActiveIndex(index)}
                        sx={{
                          cursor: 'pointer',
                          borderRadius: 3,
                          overflow: 'hidden',
                          border: index === activeIndex ? '3px solid' : '2px solid',
                          borderColor: index === activeIndex ? 'secondary.main' : 'rgba(148, 163, 184, 0.25)',
                          transform: index === activeIndex ? 'translateY(-4px)' : 'translateY(0)',
                          transition: 'all 0.25s ease',
                          boxShadow: index === activeIndex ? '0 10px 25px rgba(245, 158, 11, 0.25)' : 'none'
                        }}
                      >
                        <Box
                          component="img"
                          src={item.imageUrl}
                          alt={item.title}
                          sx={{
                            width: '100%',
                            height: smallImage ? 84 : 100,
                            objectFit: 'cover',
                            display: 'block'
                          }}
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>

            <Grid item xs={12} md={smallImage ? 8 : 7}>
              <CardContent sx={{ p: { xs: 3, md: 5 }, height: '100%' }}>
                <Stack spacing={2.5} sx={{ justifyContent: 'center', height: '100%' }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontFamily: 'Playfair Display',
                      fontWeight: 700,
                      color: 'primary.main',
                      fontSize: { xs: '1.8rem', md: '2.4rem' }
                    }}
                  >
                    {activeItem?.title}
                  </Typography>

                  {activeItem?.distance && (
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: 'secondary.main',
                        fontWeight: 700,
                        fontSize: { xs: '0.98rem', md: '1.05rem' }
                      }}
                    >
                      {activeItem.distance}
                    </Typography>
                  )}

                  <Typography
                    variant="body1"
                    sx={{
                      color: '#4b5563',
                      lineHeight: 1.95,
                      whiteSpace: 'pre-line',
                      fontSize: { xs: '1rem', md: '1.05rem' },
                      textAlign: 'justify'
                    }}
                  >
                    {activeItem?.description}
                  </Typography>
                </Stack>
              </CardContent>
            </Grid>
          </Grid>
        </Card>
      </Container>
    </Box>
  );
};

export default InfoListingPage;
